// Asistente con IA del panel (plan Pro). El dueño escribe "subí la muzza 10%" o "cambiá el
// horario", el workflow "MiQRapp - Asistente" de n8n (n8n/miqrapp-asistente.js) se lo pasa a la IA
// y la IA devuelve propuestas de cambios. La IA no escribe nada: acá se revisa cada propuesta con
// las mismas reglas del resto del panel y se muestra cómo queda; recién se guarda cuando el dueño
// toca "Confirmar", con las mismas funciones de useBusiness que usan las otras pantallas.
import { MAX_PRICE, ROUNDINGS, changeProblem, planPriceChange, priceNumber } from "./bulkPrice.js";
import { validateBusiness } from "./businessValidation.js";
import { formatPrice, whatsappNumber } from "./menu.js";
import { openStatus } from "./hours.js";

export const ASSISTANT_URL = "https://n8n.miqrapp.com/webhook/miqrapp/asistente";
export const MAX_MESSAGE = 500;
// Mensajes anteriores que viajan con cada pedido, para que entienda "y la grande también".
export const HISTORY_SIZE = 8;
// Filas que se ven en la tarjeta antes de "y N más".
export const CARD_ROWS = 6;

// Datos del negocio que el asistente puede cambiar, en el orden del formulario.
export const BUSINESS_FIELDS = [
    ["nombreEmpresa", "Nombre"],
    ["descripcionEmpresa", "Descripción"],
    ["calle", "Calle"],
    ["altura", "Número"],
    ["ciudad", "Ciudad"],
    ["provincia", "Provincia"],
    ["telefono", "WhatsApp"],
    ["horario", "Horario principal"],
    ["horarioSec", "Otro horario"]
];
const FIELD_KEYS = BUSINESS_FIELDS.map(([key]) => key);

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
const lower = (s) => String(s ?? "").trim().toLowerCase();
const showPhone = (t) => /^[0-9]+$/.test(t || "") ? "+" + t : (t || "");

// Lo que viaja a n8n como contexto. Los productos van con una referencia corta (p1, p2...)
// en vez del id largo: gasta menos y la IA se equivoca menos. `refs` traduce la vuelta.
export const buildContext = (business, products, categories) => {
    const refs = new Map();
    const productos = products.map((p, i) => {
        const ref = "p" + (i + 1);
        refs.set(ref, p.id);
        return {
            ref,
            nombre: String(p.name ?? ""),
            categoria: String(p.category ?? ""),
            precio: priceNumber(p.price),
            agotado: p.agotado === true
        };
    });
    const negocio = Object.fromEntries(FIELD_KEYS.map((key) => [key, String(business[key] ?? "")]));
    negocio.pedidos = business.pedidos === true;
    return { negocio, categorias: [...categories], productos, refs };
}

// La conversación anterior, recortada, en el formato que espera el workflow.
export const buildHistory = (messages) => messages
    .filter((m) => m.texto && !m.error)
    .slice(-HISTORY_SIZE)
    .map((m) => ({
        rol: m.rol === "usuario" ? "usuario" : "asistente",
        texto: (m.texto + (m.acciones?.length ? " [Propuestas: " + m.acciones.map((a) => a.resumen + " (" + (a.estado || "pendiente") + ")").join("; ") + "]" : "")).slice(0, 1200)
    }));

const TIPOS = ["precios", "agotado", "datos", "pedidos"];

// Pasa una acción como la devuelve la IA a la forma del panel, con ids de verdad.
// Las referencias que no existen se descartan. null si la acción no tiene sentido.
export const normalizeAction = (raw, refs) => {
    if (!raw || !TIPOS.includes(raw.tipo)) return null;
    const ids = [...new Set((Array.isArray(raw.productos) ? raw.productos : []).map((r) => refs.get(String(r))).filter(Boolean))];
    const alcance = ["todo", "categoria", "productos"].includes(raw.alcance) ? raw.alcance : (ids.length ? "productos" : null);
    const base = { tipo: raw.tipo, alcance, categoria: raw.categoria ? String(raw.categoria) : "", ids };
    if (raw.tipo === "precios") {
        return {
            ...base,
            modo: ["porcentaje", "monto", "fijar"].includes(raw.modo) ? raw.modo : null,
            direccion: raw.direccion === "bajar" ? "bajar" : "subir",
            valor: typeof raw.valor === "number" && Number.isFinite(raw.valor) ? Math.round(raw.valor * 100) / 100 : null,
            redondeo: ROUNDINGS.includes(raw.redondeo) ? raw.redondeo : 0
        };
    }
    if (raw.tipo === "agotado") return { ...base, agotado: raw.agotado !== false };
    if (raw.tipo === "pedidos") return typeof raw.activar === "boolean" ? { tipo: "pedidos", activar: raw.activar } : null;
    const datos = raw.datos && typeof raw.datos === "object" ? raw.datos : {};
    const cambios = Object.fromEntries(FIELD_KEYS.filter((k) => typeof datos[k] === "string").map((k) => [k, datos[k]]));
    return Object.keys(cambios).length ? { tipo: "datos", cambios } : null;
}

// Qué productos toca una acción de precios o agotados.
const scopeOf = (action, products, categories) => {
    if (action.alcance === "todo") return { list: products, where: "todo el catálogo" };
    if (action.alcance === "categoria") {
        const name = categories.find((c) => lower(c) === lower(action.categoria)) || products.find((p) => lower(p.category) === lower(action.categoria))?.category;
        if (!name) return { list: [], where: action.categoria, problem: `No encontramos la categoría "${action.categoria}".` };
        return { list: products.filter((p) => p.category === name), where: name };
    }
    const ids = new Set(action.ids);
    const list = products.filter((p) => ids.has(p.id));
    return { list, where: list.length === 1 ? list[0].name : plural(list.length, "producto", "productos") };
}

const priceRow = (product, before, after, note) => ({ key: product.id, label: product.name, sub: product.category, before: formatPrice(before ?? product.price), after: after === null ? null : formatPrice(after), up: after !== null && before !== null ? after > before : null, note });

const planPrices = (action, { products, categories }) => {
    const { list, where, problem: scopeProblem } = scopeOf(action, products, categories);
    const verb = action.direccion === "bajar" ? "Bajar" : "Subir";
    const amount = action.valor === null ? "" : String(action.valor);
    let title;
    if (action.modo === "fijar") title = `Precio ${formatPrice(action.valor)} · ${where}`;
    else if (action.modo === "porcentaje") title = `${verb} ${amount.replace(".", ",")}% · ${where}`;
    else title = `${verb} ${formatPrice(action.valor)} · ${where}`;
    if (action.redondeo && action.modo !== "fijar") title += ` · redondeo a $${action.redondeo}`;

    const fail = (problem) => ({ tipo: "precios", title, rows: [], problem, update: [] });
    if (scopeProblem) return fail(scopeProblem);
    if (!list.length) return fail("No encontramos los productos para cambiar.");
    if (!action.modo || action.valor === null) return fail("No quedó claro cuánto cambiar el precio.");

    let update;
    let rows;
    let skipped = 0;
    let same = 0;
    if (action.modo === "fijar") {
        if (!(action.valor > 0) || action.valor >= MAX_PRICE) return fail("El precio tiene que ser mayor a $0.");
        rows = [];
        update = [];
        list.forEach((product) => {
            const before = priceNumber(product.price);
            if (before === action.valor) { same++; return; }
            update.push({ product, price: action.valor });
            rows.push(priceRow(product, before, action.valor));
        });
    } else {
        const change = { kind: action.modo === "porcentaje" ? "percent" : "fixed", direction: action.direccion === "bajar" ? "down" : "up", amount, rounding: action.redondeo };
        const problem = changeProblem(change);
        if (problem) return fail(problem);
        const plan = planPriceChange(list, change);
        update = plan.update;
        skipped = plan.skipped;
        same = plan.same;
        rows = plan.rows.filter((r) => r.status === "change").map((r) => priceRow(r.product, r.before, r.after));
    }
    const notes = [];
    if (same) notes.push(plural(same, "producto ya tiene ese precio", "productos ya tienen ese precio"));
    if (skipped) notes.push(plural(skipped, "producto no se puede cambiar (precio vacío o inválido)", "productos no se pueden cambiar (precio vacío o inválido)"));
    if (!update.length) return { ...fail(same ? "Los precios ya están así." : "Ningún precio se puede cambiar así."), notes };
    return { tipo: "precios", title, rows, notes, update, description: "Asistente: " + title, confirmLabel: update.length === 1 ? "Cambiar 1 precio" : `Cambiar ${update.length} precios` };
}

const planAgotado = (action, { products, categories }) => {
    const { list, where, problem } = scopeOf(action, products, categories);
    const title = (action.agotado ? "Marcar agotado" : "Volver a disponible") + " · " + where;
    if (problem) return { tipo: "agotado", title, rows: [], problem, update: [] };
    const update = list.filter((p) => (p.agotado === true) !== action.agotado);
    const rows = update.map((p) => ({ key: p.id, label: p.name, sub: p.category, before: action.agotado ? "Disponible" : "Agotado", after: action.agotado ? "Agotado" : "Disponible" }));
    if (!list.length) return { tipo: "agotado", title, rows, problem: "No encontramos esos productos.", update };
    if (!update.length) return { tipo: "agotado", title, rows, problem: action.agotado ? "Ya están marcados como agotados." : "Ya están disponibles.", update };
    const already = list.length - update.length;
    const notes = already ? [action.agotado ? plural(already, "ya estaba agotado", "ya estaban agotados") : plural(already, "ya estaba disponible", "ya estaban disponibles")] : [];
    return { tipo: "agotado", title, rows, notes, update, agotado: action.agotado, confirmLabel: action.agotado ? "Marcar agotado" : "Marcar disponible" };
}

const planDatos = (action, { business }) => {
    const current = Object.fromEntries(FIELD_KEYS.map((k) => [k, String(business[k] ?? "")]));
    const merged = { ...current, ...action.cambios };
    const result = validateBusiness(merged);
    const changed = FIELD_KEYS.filter((k) => k in action.cambios && (result.data[k] ?? merged[k]) !== current[k]);
    const title = changed.length ? "Cambiar " + changed.map((k) => BUSINESS_FIELDS.find(([key]) => key === k)[1].toLowerCase()).join(", ") : "Datos del negocio";
    const rows = changed.map((k) => ({
        key: k,
        label: BUSINESS_FIELDS.find(([key]) => key === k)[1],
        before: (k === "telefono" ? showPhone(current[k]) : current[k]) || "(vacío)",
        after: (k === "telefono" ? showPhone(result.data[k] ?? merged[k]) : (result.data[k] ?? merged[k])) || "(vacío)"
    }));
    if (!changed.length) return { tipo: "datos", title, rows, problem: "Esos datos ya están así." };
    const errorKey = Object.keys(result.errors || {})[0];
    if (errorKey) return { tipo: "datos", title, rows, problem: result.errors[errorKey] };
    const notes = [];
    if ((changed.includes("horario") || changed.includes("horarioSec")) && !openStatus(result.data)) {
        notes.push('Tu menú no va a poder mostrar "Abierto ahora" con este horario. Si querés, pedímelo con días y horas, por ejemplo "Lunes a viernes de 8 a 20".');
    }
    return { tipo: "datos", title, rows, notes, fields: result.data, confirmLabel: "Guardar cambios" };
}

const planPedidos = (action, { business }) => {
    const title = action.activar ? "Activar pedidos por WhatsApp" : "Desactivar pedidos por WhatsApp";
    const rows = [{ key: "pedidos", label: "Pedidos por WhatsApp", before: business.pedidos ? "Activados" : "Desactivados", after: action.activar ? "Activados" : "Desactivados" }];
    if ((business.pedidos === true) === action.activar) return { tipo: "pedidos", title, rows, problem: action.activar ? "Los pedidos ya están activados." : "Los pedidos ya están desactivados." };
    if (action.activar && !whatsappNumber(business.telefono)) return { tipo: "pedidos", title, rows, problem: "Primero cargá el WhatsApp del negocio." };
    return { tipo: "pedidos", title, rows, activar: action.activar, confirmLabel: action.activar ? "Activar pedidos" : "Desactivar pedidos" };
}

// Cómo queda una acción con los datos de ahora: título, filas antes/después, avisos y lo
// necesario para guardarla. Si no se puede aplicar, `problem` dice por qué.
export const planAction = (action, ctx) => {
    if (action.tipo === "precios") return planPrices(action, ctx);
    if (action.tipo === "agotado") return planAgotado(action, ctx);
    if (action.tipo === "datos") return planDatos(action, ctx);
    return planPedidos(action, ctx);
}

// Respuesta del workflow → { texto, acciones } listas para el chat.
export const readReply = (result, refs, ctx) => {
    const texto = String(result?.respuesta ?? "").trim();
    const acciones = (Array.isArray(result?.acciones) ? result.acciones : [])
        .slice(0, 6)
        .map((raw) => normalizeAction(raw, refs))
        .filter(Boolean)
        .map((action, i) => ({ ...action, id: Date.now() + "-" + i, estado: "pendiente", resumen: planAction(action, ctx).title }));
    return { texto: texto || (acciones.length ? "Esto es lo que cambiaría:" : "No entendí bien. ¿Me lo decís de otra forma?"), acciones };
}

// Ideas para arrancar, con nombres del propio catálogo.
export const suggestions = (products, categories) => {
    const used = categories.filter((c) => products.some((p) => p.category === c));
    const first = products.find((p) => p.name)?.name;
    return [
        used[0] ? `Subí un 10% todo lo de ${used[0]}` : "Subí un 10% todos los precios",
        first ? `Marcá ${first} como agotado` : null,
        "Cambiá el horario a martes a domingo de 19 a 24",
        "¿Qué fue lo más visto esta semana?"
    ].filter(Boolean);
}

// Manda el mensaje a n8n. Devuelve { respuesta, acciones } o tira un Error con un mensaje para el dueño.
// `audio` ({ type, data } en base64) va en vez de `mensaje`: n8n lo pasa a texto y lo devuelve en `transcripcion`.
// `estadisticas` (assistantStats.js) es el resumen de visitas y pedidos para responder preguntas; puede ser null.
export const askAssistant = async ({ idToken, mensaje, audio, historial, context, estadisticas, signal }) => {
    const { refs, ...contexto } = context;
    let response;
    try {
        response = await fetch(ASSISTANT_URL, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ idToken, mensaje: mensaje || "", audio, historial, ...contexto, ...(estadisticas && { estadisticas }) }),
            signal
        });
    } catch (error) {
        if (error.name === "AbortError") throw error;
        throw new Error("No pudimos conectarnos. Revisá tu internet y probá de nuevo.");
    }
    let result = {};
    try {
        result = await response.json();
    } catch (error) {
        // Sin cuerpo: se usa el mensaje genérico de abajo.
    }
    if (!response.ok || typeof result.respuesta !== "string") {
        throw new Error(result.error || "El asistente no pudo responder. Probá de nuevo en unos minutos.");
    }
    return result;
}
